<template>
  <div
    :class="[!createOrEdit ? 'vx-card' : '', 'p-6']">
    <reviews-settings-create-or-edit
      v-if="createOrEdit"
      class="mb-base"
      @cancel="createOrEdit = false"/>

    <template v-else>
      <base-ag-grid-header
        :rows-selected-count="rowsSelectedCountWithAllSelection"
        :filters-count="filtersAppliedCount"
        :filters-match.sync="filtersMatch"
        @reset-selection="setSelectedRowsNone()"
        @reset-filters="resetFilters()">
        <template v-slot:header-left>
          <items-per-page
            :count="count"
            :currentPage="currentPage"
            :paginationPageSize="paginationPageSize"
            @changed-page-size="paginationSetPageSize">
          </items-per-page>

          <list-action-drop-down
            v-if="isAnyRowsSelected"
            :options="multipleActionOptions"
            class="ml-3"
            @export="exportData()"
            @delete="confirmDeleteRecords()">
          </list-action-drop-down>
        </template>

        <template v-slot:header-right>
          <div class="w-full">
            <div class="flex w-full items-center justify-between md:justify-end">
              <div
                class="flex flex-col justify-center items-center mr-base pr-base border-l-0 border-r-2 border-grey border-solid border-t-0 border-b-0 cursor-pointer"
                @click="$router.push('analytics/reviews')"
              >
                <p
                  class="text-center font-bold">
                  {{ overallRating | fixed_number(1) }}
                </p>
                <div
                  class="flex justify-center">
                  <star-rating
                    :rating="overallRating"
                    :increment="0.1"
                    :round-start-rating="false"
                    :show-rating="false"
                    read-only
                    :star-size="20"/>
                </div>
              </div>

              <div class="flex items-center">
                <vs-button
                  color="primary"
                  size="small"
                  icon-pack="feather"
                  icon="icon-settings"
                  class="mr-3"
                  @click="createOrEdit = true">{{ googleReviewSettingsText }}
                </vs-button>

                <vs-button
                  radius
                  color="primary"
                  type="border"
                  icon-pack="feather"
                  icon="icon-refresh-cw"
                  @click="refreshData()">
                </vs-button>
              </div>
            </div>
            <div class="flex vw-full lg:hidden justify-end">
              <div class="col-gap-2 flex mt-5">
                <label for="">{{ $t('$General.Anonymous') }}</label>
                <vs-switch v-model="includeAnonymousReviews"/>
              </div>
            </div>
          </div>
        </template>
      </base-ag-grid-header>

      <selection-alert-info
        v-model="selectionPageAlert"
        class="mb-5"
        :entity="entity"
        :count="count"
        :rows-selected-count="rowsSelectedCount"
        @select-all="setSelectedRowsAll()"></selection-alert-info>

      <export-alert-info
        class="mb-5"
        :entity="$tc(`$Entities.${entity}`, 2)"
        :exporting.sync="exportingList"
        :exported.sync="exportedList"
        :fileUrl="urlFileExported"
        :send-email="exportSendEmail"/>

      <ag-grid-vue
        :key="agGridKey"
        ref="agGridTable"
        :domLayout="domLayout"
        :style="gridStyle"
        :components="components"
        :frameworkComponents="frameworkComponents"
        :gridOptions="gridOptions"
        class="ag-theme-material w-100 ag-grid-table"
        :columnDefs="columnDefs"
        :defaultColDef="defaultColDef"
        :column-types="columnTypes"
        :getRowNodeId="getRowNodeId"
        :autoParamsRefresh="true"
        rowSelection="multiple"
        :animateRows="true"
        :suppressRowClickSelection="true"
        rowModelType="infinite"
        :pagination="hasPagination"
        :paginationPageSize="paginationPageSize"
        :suppressPaginationPanel="suppressPaginationPanel"
        :overlayLoadingTemplate="overlayLoadingTemplate"
        :noRowsOverlayComponent="noRowsOverlayComponent"
        :noRowsOverlayComponentParams="noRowsOverlayComponentParams"
        :maxConcurrentDatasourceRequests="maxConcurrentDatasourceRequests"
        :cacheBlockSize="cacheBlockSize"
        :maxBlocksInCache="maxBlocksInCache"
        :getRowHeight="getRowHeight"
        @selection-changed="onSelectionChanged"
        @grid-ready="onGridReady"
        @model-updated="onModelUpdate"
        @grid-size-changed="onGridSizeChanged"
        @first-data-rendered="onFirstDataRendered"
        @row-double-clicked="onRowDoubleClicked"
        @filter-changed="onFilterChanged"
        @pagination-changed="onPaginationChanged"
        @row-clicked="onRowClicked"
      >
      </ag-grid-vue>
    </template>

    <vs-popup
      :title="exportModalTitle"
      :active.sync="activeModalToExport">
      <transition name="zoom-fade">
        <export-json-to-excel
          v-if="showExportComponent"
          :columns="getColumnsToExport"
          :data="rowsSelected"
          @close="activeModalToExport=false"
          @export="onExport">
        </export-json-to-excel>
      </transition>
    </vs-popup>

    <vs-popup
      :title="detailsModalTitle"
      :active.sync="activeDetailsModal">
      <transition name="zoom-fade">
        <div v-if="activeDetailsModal">
          <cell-renderer-contact
            class="text-center"
            :review-payload="reviewDetails"
            :rating-centered="true"
          />

          <p class="mt-1 text-center">{{ reviewDetails.updatedAt | date_format(
            'DD MMM YYYY',
            true,
            this.tenantAccountDetailsTimezone,
          ) }}</p>

          <p class="mt-base text-center">{{ reviewDetails.review }}</p>
        </div>
      </transition>
    </vs-popup>
  </div>
</template>

<script>
import '@/assets/scss/vuexy/extraComponents/agGridStyleOverride.scss';
import { AgGridVue } from 'ag-grid-vue';
import StarRating from 'vue-star-rating';

// Services
import reviewService from '@/api/modules/review.service';

// Mixins
import commonListCreateOrEditWithAgGrid from '@/views/modules/_mixins/commonListCreateOrEditWithAgGrid';

// Cell Renderer
import CellRendererActions from '@/views/modules/_components/cell-renderer/SingleCellRendererActions.vue';
import CellRendererRating from '@/views/modules/reviews/ReviewsSettingsCellRendererRating.vue';
import CellRendererContact from '@/views/modules/reviews/ReviewsSettingsCellRendererContact.vue';

// Custom Components
import AgGridSelectionHeader from '@/views/modules/_components/AgGridSelectionHeader';
import ReviewsSettingsCreateOrEdit from '@/views/modules/reviews/ReviewsSettingsCreateOrEdit.vue';
import ListActionDropDown from '@/views/modules/_components/ListActionDropDown.vue';
import BaseAgGridHeader from '@/views/modules/_components/BaseAgGridHeader.vue';
import ExportAlertInfo from '@/views/modules/_components/ExportAlertInfo.vue';
import ItemsPerPage from '@/views/modules/_components/ItemsPerPage.vue';
import SelectionAlertInfo from '@/views/modules/SelectionAlertInfo.vue';
import ExportJsonToExcel from '@/views/modules/_components/ExportJsonToExcel.vue';
import CellRendererLargeText from '@/views/modules/_components/cell-renderer/CellRendererLargeTextModal.vue';
import CustomNoRowsOverlay from '@/views/modules/_components/customAgGridNoRowsOverlay';
/**
 * @module views/modules/reviews/ReviewsSettings
 * @author Dilan Useche <dilan8810@gmail.com>
 */
export default {
  name: 'ReviewsSettings',
  i18n: {
    messages: {
      en: {
        GoogleReviewSettings: 'Google reviews',
        ThereAreNoReviews: 'No reviews to display',
        $NoRowsOverlay: {
          Title: 'No reviews to display',
          Text: 'There are no reviews to view right now. Create a new campaign to start collecting reviews.',
          Button: 'Create new campaign',
        },
        DetailsModalTitle: 'Review from {name}',
      },
    },
  },
  components: {
    ExportJsonToExcel,
    SelectionAlertInfo,
    ItemsPerPage,
    ExportAlertInfo,
    BaseAgGridHeader,
    ListActionDropDown,
    AgGridVue,
    StarRating,
    ReviewsSettingsCreateOrEdit,
    // eslint-disable-next-line vue/no-unused-components
    CellRendererActions,
    // eslint-disable-next-line vue/no-unused-components
    CellRendererRating,
    // eslint-disable-next-line vue/no-unused-components
    CellRendererLargeText,
    CellRendererContact,
  },
  mixins: [commonListCreateOrEditWithAgGrid],
  data() {
    return {
      entity: this.$enums.Entity.REVIEW,
      exportCollectionFunction: this.exportReviews,
      overviewData: null,
      preferFitColumns: true,
      visibleTabletColumns: ['contact', 'feedback'],
      visibleLaptopColumns: ['contact', 'feedback', 'updatedAt'],
      gridOptions: {
        rowHeight: 58,
        context: {
          componentParent: this,
        },
      },
      reviewDetails: null,
      noRowsMessage: this.$t('ThereAreNoReviews'),
      activeDetailsModal: false,

      columnDefs: [
        {
          colId: 'contact',
          headerName: this.$t('$General.Contact'),
          field: 'name',
          filter: true,
          sortable: false,
          minWidth: 350,
          maxWidth: 450,
          checkboxSelection: true,
          headerComponentParams: { checkboxSelection: true },
          cellClass: 'mobile-caret',
          cellRendererFramework: 'CellRendererContact',
        },
        {
          colId: 'score',
          headerName: this.$t('$General.Rating'),
          field: 'score',
          filter: 'agNumberColumnFilter',
          width: 250,
          headerClass: 'flex justify-center',
          cellRendererFramework: 'CellRendererRating',
        },
        {
          colId: 'feedback',
          headerName: this.$t('$General.Feedback'),
          field: 'review',
          filter: true,
          sortable: false,
          minWidth: 250,
          maxWidth: 1906,
          cellClass: 'flex items-center',
          cellRendererFramework: 'CellRendererLargeText',
        },
      ],
      components: {
        CellRendererActions,
        CellRendererLargeText,
      },
      frameworkComponents: {
        agColumnHeader: AgGridSelectionHeader,
        customNoRowsOverlay: CustomNoRowsOverlay,
      },

      createOrEdit: false,
      includeAnonymousReviews: false,
    };
  },
  computed: {
    overallRating() {
      return this.overviewData ? this.overviewData.rating : 0;
    },
    multipleActionOptions() {
      return this.defaultMultipleActionOptions.filter(
        (option) => option.event !== this.$enums.Operation.DELETE,
      );
    },
    googleReviewSettingsText() {
      return this.gpMqIsMobileOrTablet ? '' : this.$t('GoogleReviewSettings');
    },
    detailsModalTitle() {
      if (!this.reviewDetails) return '';

      return this.$t('DetailsModalTitle', {
        name: this.reviewDetails.name || this.$t('$General.Anonymous').toLowerCase(),
      });
    },
  },
  created() {
    this.fetchOverviewData();
  },
  beforeMount() {
    const $this = this;
    this.noRowsOverlayComponent = 'customNoRowsOverlay';
    this.noRowsOverlayComponentParams = {
      title: this.$t('$NoRowsOverlay.Title'),
      text: this.$t('$NoRowsOverlay.Text'),
      buttonText: this.$t('$NoRowsOverlay.Button'),
      async actionButtonClicked() {
        await $this.$router.push({
          name: 'sms-campaigns-create',
        });
      },
    };

    this.gridOptions.rowHeight = this.gpMqIsLessOrEqualLaptopMedium ? 112 : 84;
  },
  methods: {
    async fetchOverviewData() {
      const { data } = await reviewService.getReviewsOverview();
      this.overviewData = data;
    },
    async fetchAgGridData(agGridParams) {
      const params = {
        populate: [
          {
            path: 'contact',
            select: 'id name email phoneInternational',
          },
        ],
        ...agGridParams,
      };
      let filters = {
        ...params.filters,
      };
      let { filtersMatch } = params;

      const { data } = await reviewService.getAll({
        ...params,
        filters,
        filtersMatch,
      });
      return data;
    },
    async exportReviews(exportInfo) {
      const resp = await reviewService.exportFile(exportInfo);
      return resp.data;
    },
    getRowHeight(params) {
      return this.gpMqIsLessOrEqualLaptopMedium ? 108 : 80;
    },
    async refreshData() {
      await this.resetDataSourceAndSelection();
      await this.fetchOverviewData();
    },
    onRowClicked(params) {
      if (this.gpMqIsMobile) {
        this.reviewDetails = params.node.data;
        this.activeDetailsModal = true;
      }
    },
  },
};
</script>
